import * as THREE from 'three'

import MathsUtils from '@/utils/mathsUtils'

import BaseObject from './baseObject'
import EditingHandle from './editingHandle'
import GlassSegment from '@/model/glassSegment'

import GlassDesignerTypes from '../types'


// Editing Circle
// A circle used for adding circle punch/combine in the edge cutout tool
export default class EditingCircle extends BaseObject {
  constructor(editor, options = {}) {
    super(editor)
    this.objectClassName = 'EditingCircle'

    this.circleHandleUserTag = 'circleHandle'

    this.lineColour = '#0085F3'
    this.shapeColour = '#606060'
    this.shapeOpacity = 0.5
    this.handleColour = '#FFFFFF'

    this.position = new THREE.Vector2(0, 0)
    this.radius = 100

    this.circleMainGeom = null
    this.circleMainObj = null

    this.circleLineObj = []
    this.circleLineGeom = []
    this.circleHandleGrabPosition = new THREE.Vector2(0, 0)

    this.circleGrabPosition = new THREE.Vector2(0, 0)

    this.sizeHandle = null
    this.sizeHandleOffsetDir = new THREE.Vector2(1, 1)
    this.sizeHandleOffsetDir.normalize()

    this.listenMouseEvents = true

    this.build()
  }

  // onMouseDownEvent
  onMouseDownEvent(ev, mouseData) {
    let raycaster = mouseData.isSnapGridEnabled ? mouseData.snapRaycaster : mouseData.raycaster
    if(MathsUtils.editingPlaneIntersection(raycaster.ray, this.circleGrabPosition)) {
      this.editor.captureEventObject(this)
    }
  }

  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    // If captured, move the editing circle around
    if(this.editor.isCapturedEventObject(this)) {
      let raycaster = mouseData.isSnapGridEnabled ? mouseData.snapRaycaster : mouseData.raycaster

      // Move the editing circle around
      // Create a plane for the circle rectangle
      var intersectionPnt = new THREE.Vector3()
      if(MathsUtils.editingPlaneIntersection(raycaster.ray, intersectionPnt)) {

        // Move
        // Round to nearest mm
        this.position.set(Math.round(this.position.x + (intersectionPnt.x - this.circleGrabPosition.x)), Math.round(this.position.y + (intersectionPnt.y - this.circleGrabPosition.y)))
        this.circleGrabPosition.set(intersectionPnt.x, intersectionPnt.y)

        // Notify any listener the circle has been moved
        this.sendObjectEvent(GlassDesignerTypes.ObjectEventTypes.objectMoved, {
          position: this.position
        }) 
      }
    } else {
      if(this.editor.isSelectEnabled())
        this.editor.setMouseCursorHand()
    }

    this.editor.flagRenderRequired()
  }

  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData, intersectionInfo) {
    // If captured, release it & notify listener
    if(this.editor.isCapturedEventObject(this)) {

      this.sendObjectEvent(GlassDesignerTypes.ObjectEventTypes.objectReleased)

      this.editor.releaseCapturedObject()
    }
  }


  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()

    var renderScale = 1.0 / this.editor.camera.zoom

    this.circleMainObj.position.set(this.position.x, this.position.y, 90)
    this.circleMainObj.scale.set(this.radius, this.radius, 1)

    // Update drag handle
    this.sizeHandle.position.set(this.position.x + this.sizeHandleOffsetDir.x * this.radius,this.position.y + this.sizeHandleOffsetDir.y * this.radius, this.position.z)
  }


  /**
   * Build the initial drawing objects to display the editing line
   */
  build() {
    this.sceneObject = new THREE.Object3D()

    // Main circle shape
    this.circleMainGeom = new THREE.SphereGeometry(1, 20, 20)
    var material = new THREE.MeshBasicMaterial({
      color: this.shapeColour,
      transparent: true,
      opacity: this.shapeOpacity
    })
    this.circleMainObj = new THREE.Mesh(this.circleMainGeom, material)
    this.sceneObject.add(this.circleMainObj)

    // Radius grab handle
    this.sizeHandle = new EditingHandle(this.editor, { colour: this.handleColour })
    this.sizeHandle.userData = { type: this.circleHandleUserTag }
    this.sizeHandle.setObjectEventListener(this)

    this.addChildObject(this.sizeHandle)

    // TODO: Circle line around the edge
  }


  /**
   * Check for intersections between the object and a ray
   * Returns an object containing intersection point & object position
   * Returns null if no intersection occurred
   */
  checkRayIntersection(ray, worldRadius) {
    var rayInt = new THREE.Vector3()
    if(!MathsUtils.editingPlaneIntersection(ray, rayInt))
      return null

    let grabDistance = this.position.distanceTo(rayInt)
    if(grabDistance <= this.radius + worldRadius) {
      return {
        intersection: rayInt,
        position: new THREE.Vector2(rayInt.x, rayInt.y),
        objectId: this.id,
        distance: grabDistance
      }
    }

    return null
  }


  /**
   * Receive an object event from objects we're listening too
   */
  onReceiveObjectEvent(object, eventType, data) {
    var renderScale = 1.0 / this.editor.camera.zoom
    let objUserData = object.userData || {}

    if(eventType == GlassDesignerTypes.ObjectEventTypes.objectCaptured) {

      if(objUserData.type == this.circleHandleUserTag) {
        this.circleHandleGrabPosition.set(object.position.x, object.position.y, this.position.z)
      }
    }
    if(eventType == GlassDesignerTypes.ObjectEventTypes.objectMoved) {
      // Update size
      if(objUserData.type == this.circleHandleUserTag) {

        // Resize
        let distance = object.position.distanceTo(this.position)
        this.radius = Math.round(distance)      // Round to nearest mm

        this.sizeHandleOffsetDir = object.position.clone().sub(this.position).normalize()

        this.sendObjectEvent(GlassDesignerTypes.ObjectEventTypes.objectUpdated)

        this.editor.flagRenderRequired()
      }
    }
  }


  /**
   * Calculate the segments used to represent the editing rectangle
   * Used for clipping operations
   */
  generateSegmentList() {
    var segmentList = []
    
    // Generate 2 180deg arcs
    let segmentA = new GlassSegment(GlassDesignerTypes.SegmentTypes.arc, {
      arcRadius: this.radius,
      arcStartAngle: 0,
      arcEndAngle: 180,
      arcClockwise: true
    })
    segmentA.arcCenterPosition.set(this.position.x, this.position.y)
    segmentA.arcUpdated()
    segmentList.push(segmentA)

    let segmentB = new GlassSegment(GlassDesignerTypes.SegmentTypes.arc, {
      arcRadius: this.radius,
      arcStartAngle: 180,
      arcEndAngle: 360,
      arcClockwise: true
    })
    segmentB.arcCenterPosition.set(this.position.x, this.position.y)
    segmentB.arcUpdated()
    segmentList.push(segmentB)
    

    return segmentList
  }

}
